import React from 'react';

import { Divider } from 'antd';

import { formatDateTime } from '@/shared/lib/format/dateTime';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { MetadataItem } from '@/shared/ui/MetadataItem';
import type { RunStatus } from '@/shared/ui/RunStatusTag';
import { RunStatusTag } from '@/shared/ui/RunStatusTag';

import type { AssetDetailResponse } from '@/entities/asset-search/domain/types';

import styles from './ExperimentAssetSummaryCard.module.css';

type ExperimentAssetSummaryCardProps = {
  data: AssetDetailResponse;
  assetType?: 'run' | 'dataset' | 'feature_set' | 'parameter_set' | 'artifact';
  /** 런일 때 상위에서 배포까지 반영한 표시용 상태 (useRunDisplayStatusWithDeployment) */
  runDisplayStatus?: RunStatus | null;
  isRunDisplayStatusLoading?: boolean;
};

export const ExperimentAssetSummaryCard: React.FC<ExperimentAssetSummaryCardProps> = ({
  data,
  assetType,
  runDisplayStatus,
  isRunDisplayStatusLoading = false,
}) => {
  const creator = data.creator;
  const createdAt = data.createdAt;
  const updatedAt = data.updatedAt;

  const isSubAsset = assetType && assetType !== 'run';
  const runName = data.run?.runName || data.run?.runId;

  const displayStatus = runDisplayStatus ?? null;
  const isLoadingStatus = isRunDisplayStatusLoading;

  return (
    <div className={styles.summaryCard}>
      <div className={styles.metadata}>
        {data.project?.projectName && (
          <>
            <MetadataItem title="프로젝트">
              <EllipsisText text={data.project.projectName} className={styles.itemName} />
            </MetadataItem>
            <Divider type="vertical" className={styles.metadataDivider} />
          </>
        )}
        {data.experiment?.experimentName && (
          <>
            <MetadataItem title="실험">
              <EllipsisText text={data.experiment.experimentName} className={styles.itemName} />
            </MetadataItem>
            <Divider type="vertical" className={styles.metadataDivider} />
          </>
        )}
        {isSubAsset && runName && (
          <>
            <MetadataItem title="런">
              <EllipsisText text={runName} className={styles.itemName} />
            </MetadataItem>
            <Divider type="vertical" className={styles.metadataDivider} />
          </>
        )}
        {assetType === 'run' && displayStatus && (
          <>
            <MetadataItem title="상태">
              {isLoadingStatus ? (
                <span className={styles.itemName}>조회 중...</span>
              ) : (
                <RunStatusTag status={displayStatus} />
              )}
            </MetadataItem>
            <Divider type="vertical" className={styles.metadataDivider} />
          </>
        )}
        {creator && (
          <>
            <MetadataItem title="소유자">
              <EllipsisText text={creator} className={styles.itemName} />
            </MetadataItem>
            <Divider type="vertical" className={styles.metadataDivider} />
          </>
        )}
        {createdAt && (
          <>
            <MetadataItem title="생성일">
              <span className={styles.itemName}>{formatDateTime(createdAt)}</span>
            </MetadataItem>
            <Divider type="vertical" className={styles.metadataDivider} />
          </>
        )}
        {updatedAt && (
          <>
            <MetadataItem title="수정일">
              <span className={styles.itemName}>{formatDateTime(updatedAt)}</span>
            </MetadataItem>
          </>
        )}
      </div>
    </div>
  );
};
